import { webDataNotAsked } from '@cobliteam/api-lib';
import Button from '@cobliteam/colib-ui/Button';
import Icon from '@cobliteam/colib-ui/Icon';
import Spinner from '@cobliteam/colib-ui/Spinner';
import { Location, UserRole } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { useTitle } from '@cobliteam/hooks';
import useSessionStore from '@cobliteam/hooks/src/useSession/store/useSessionStore';
import { useTranslation } from '@cobliteam/i18n';
import { history, loginResetState } from '@cobliteam/store';
import { Suspense, useLayoutEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import LoginMKT from '../../components/LoginMKT';

const AuthLayout = ({ children }: any) => {
  const { t } = useTranslation();
  useTitle('Login');
  const dispatch = useDispatch();
  const session = useSessionStore((s) => s.session);
  const { pathname } = useLocation();
  const isHome = pathname === Location.LOGIN;

  // `dark` no <html> cobre body, toasts e popovers, que renderizam fora deste layout; ao sair volta o tema salvo.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.add('dark');
    return () => {
      root.classList.toggle('dark', wasDark);
    };
  }, []);

  if (session) {
    // Frota inativa entra direto na única página acessível
    if (session.fleet.active === false) return <Redirect to={Location.BUSINESS_MANAGER_BOLETOS} />;
    return session.user.role === UserRole.MAP_VIEW ? <Redirect to={Location.MONITORING} /> : <Redirect to={Location.HOME} />;
  }

  const handleGoBack = () => {
    if (pathname === Location.TOKEN_VALIDATION) {
      dispatch(
        loginResetState({
          forgetPasswordWebData: webDataNotAsked(),
          validateTokenWebData: webDataNotAsked(),
          token: '',
        })
      );
    }

    history.goBack();
  };

  return (
    // O login é sempre escuro, seja qual for o tema salvo. Formulário vem primeiro no DOM para o Tab chegar nele antes do banner; a ordem visual inverte no tablet+.
    <div className='dark scheme-dark flex min-h-screen tablet:flex-row-reverse bg-background text-foreground'>
      <section className='flex flex-1 flex-col items-center justify-center bg-linear-to-b from-neutral-800 via-neutral-900 to-neutral-950 px-6 py-8'>
        <div className='flex w-full max-w-[400px] flex-col'>
          <img src='/static/images/cobli-logo-horizontal-white.svg' alt='Cobli' className='mb-10 tablet:hidden h-10 w-auto self-start' />

          {!isHome && (
            <Button variant='ghost' size='sm' onClick={handleGoBack} className='mb-4 -ml-2.5 self-start text-muted-foreground hover:text-foreground'>
              <Icon size='sm' name='arrow-left' />
              Voltar
            </Button>
          )}

          <Suspense
            fallback={
              <div className='flex min-h-80 items-center justify-center'>
                <Spinner className='size-6' aria-label={t('common:messages.loading')} />
              </div>
            }
          >
            {children}
          </Suspense>
        </div>
      </section>

      <section className='relative tablet:flex hidden tablet:w-1/2'>
        <img src='/static/images/cobli-logo-horizontal-white.svg' alt='Cobli' className='absolute top-8 left-8 z-10 h-10 w-auto' />
        <ErrorBoundary
          onError={(error, errorInfo) => {
            DatadogTrack.error(error, {
              component: 'AuthLayout',
              errorInfo,
            });
          }}
          fallbackRender={() => <div className='flex size-full items-center justify-center bg-royal-950 text-white/80'>Erro ao carregar a página</div>}
        >
          <LoginMKT />
        </ErrorBoundary>
      </section>
    </div>
  );
};

export default AuthLayout;
